"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/steps/NameQuizStep";

import type { FC, MouseEvent, TouchEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { cn } from "utils";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { EdHeroBubble } from "../quiz/EdHeroBubble";
import { QuizContinueButton } from "../quiz/QuizChoiceOption";
import { QuizProgressHeader, QuizStepLayout } from "../quiz/QuizStepLayout";
import {
  ONBOARDING_FLOATING_CTA_BOTTOM_CLASS,
  ONBOARDING_FLOATING_CTA_INSET_NARROW_CLASS,
  ONBOARDING_QUIZ_PC_INPUT_TO_CTA_GAP_CLASS,
} from "../shared/onboardingFloatingCtaTokens";
import { QUIZ_COMMON_COPY } from "../onboardingWizardCopy";
import styles from "./nameQuizInput.module.css";
import { useKeyboardInsets } from "@/src/hooks";
import { useSaveOwnerNickName } from "@/src/hooks/useSaveOwnerNickName";

const I18n = I18nUtil.createScoped(Translations);

/** Figma Name — Desktop (96344:110242): Prompt bottom → input = 134px. */
const NAME_QUIZ_PC_PROMPT_TO_INPUT_GAP_CLASS = "md:mt-[109px]";

/** Touch hit area — taller than text caret for easy re-focus taps. */
const NAME_QUIZ_INPUT_HIT_AREA_CLASS = "min-h-20";
const NAME_QUIZ_INPUT_HIT_AREA_ENABLED_CLASS = "cursor-text";
/** Decorative caret — match headline_regular_m line-height (32px), not hit-area height. */
const NAME_QUIZ_DECORATIVE_CARET_CLASS = "h-8 w-0.5";

/** iOS: keep compact layout briefly after keyboard hide to avoid reflow under finger. */
const NAME_QUIZ_REFLOW_SUPPRESS_MS = 600;

interface NameQuizStepProps {
  value: string;
  progressIndex: number;
  progressTotal: number;
  onBack: () => void;
  onContinue: (name: string) => void;
}

export const NameQuizStep: FC<NameQuizStepProps> = ({
  value,
  progressIndex,
  progressTotal,
  onBack,
  onContinue,
}) => {
  const [name, setName] = useState(value);
  const [refocusSuppressed, setRefocusSuppressed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  /** Block hit-area refocus while keyboard-dismiss gesture is still finishing. */
  const suppressInputRefocusRef = useRef(false);
  const suppressInputRefocusTimerRef = useRef<number | undefined>(undefined);
  const prevKeyboardVisibleRef = useRef(false);
  const { device } = useParams<{ device: DeviceType }>();
  /** Keyboard avoid / compact layout: App iOS & Android only. */
  const isAppMobile = device === "ios" || device === "android";
  const { saveNickNameAsync, isSaving } = useSaveOwnerNickName();
  const keyboardInsets = useKeyboardInsets();
  const useWebKeyboardInset = keyboardInsets.shouldApplyWebInsetPadding;
  /**
   * Compact top-aligned layout when soft keyboard is up on App mobile.
   * Also hold compact briefly after dismiss so iOS reflow does not move the
   * input under the same finger position and re-trigger native focus.
   */
  const collapseForKeyboard =
    isAppMobile && (keyboardInsets.visible || refocusSuppressed);
  /** Only Phase 2 web-inset may add keyboard padding (avoid double-lift). */
  const liftCtaForKeyboard = isAppMobile && useWebKeyboardInset;

  useEffect(() => {
    setName(value);
  }, [value]);

  const suppressInputRefocus = useCallback(
    (ms = NAME_QUIZ_REFLOW_SUPPRESS_MS) => {
      suppressInputRefocusRef.current = true;
      setRefocusSuppressed(true);
      if (suppressInputRefocusTimerRef.current) {
        window.clearTimeout(suppressInputRefocusTimerRef.current);
      }
      suppressInputRefocusTimerRef.current = window.setTimeout(() => {
        suppressInputRefocusRef.current = false;
        setRefocusSuppressed(false);
        suppressInputRefocusTimerRef.current = undefined;
      }, ms);
    },
    [],
  );

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!suppressInputRefocusRef.current) {
        inputRef.current?.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const root = formRef.current;
    if (!root || !isAppMobile) {
      return;
    }

    const onTouchStart = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (inputRef.current?.contains(target)) {
        return;
      }
      const inputFocused = document.activeElement === inputRef.current;
      if (keyboardInsets.visible || inputFocused) {
        suppressInputRefocus(NAME_QUIZ_REFLOW_SUPPRESS_MS);
        inputRef.current?.blur();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!suppressInputRefocusRef.current) {
        return;
      }
      if (event.target !== inputRef.current) {
        return;
      }
      requestAnimationFrame(() => {
        inputRef.current?.blur();
      });
    };

    root.addEventListener("touchstart", onTouchStart, { capture: true });
    root.addEventListener("focusin", onFocusIn, true);
    return () => {
      root.removeEventListener("touchstart", onTouchStart, true);
      root.removeEventListener("focusin", onFocusIn, true);
    };
  }, [isAppMobile, keyboardInsets.visible, suppressInputRefocus]);

  useEffect(() => {
    return () => {
      if (suppressInputRefocusTimerRef.current) {
        window.clearTimeout(suppressInputRefocusTimerRef.current);
      }
    };
  }, []);

  /** iOS may scroll the page to the focused input; pin scroll when compact. */
  useEffect(() => {
    if (!collapseForKeyboard) {
      return;
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [collapseForKeyboard]);

  const trimmed = name.trim();
  const canContinue = trimmed.length > 0;

  /**
   * Keyboard hide triggers layout reflow (compact → centered). The same tap can
   * land on the input hit-area after reflow and re-open the keyboard unless we
   * suppress refocus briefly after dismiss.
   */
  useEffect(() => {
    const wasVisible = prevKeyboardVisibleRef.current;
    const nowVisible = keyboardInsets.visible;
    if (wasVisible && !nowVisible) {
      suppressInputRefocus(NAME_QUIZ_REFLOW_SUPPRESS_MS);
    }
    prevKeyboardVisibleRef.current = nowVisible;
  }, [keyboardInsets.visible, suppressInputRefocus]);

  const handleContinue = useCallback(async () => {
    if (!canContinue) {
      return;
    }
    suppressInputRefocus(NAME_QUIZ_REFLOW_SUPPRESS_MS);
    inputRef.current?.blur();
    await saveNickNameAsync(trimmed);
    onContinue(trimmed);
  }, [
    canContinue,
    onContinue,
    saveNickNameAsync,
    suppressInputRefocus,
    trimmed,
  ]);

  const focusNameInput = useCallback(() => {
    if (collapseForKeyboard || suppressInputRefocusRef.current) {
      return;
    }
    inputRef.current?.focus({ preventScroll: true });
  }, [collapseForKeyboard]);

  const handleHitAreaMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      event.preventDefault();
      focusNameInput();
    },
    [focusNameInput],
  );

  const handleHitAreaClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      focusNameInput();
    },
    [focusNameInput],
  );

  const handleContinueTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isAppMobile) {
        return;
      }
      suppressInputRefocus(NAME_QUIZ_REFLOW_SUPPRESS_MS);
      inputRef.current?.blur();
      event.preventDefault();
    },
    [isAppMobile, suppressInputRefocus],
  );

  const handleContinueTouchEnd = useCallback(() => {
    if (!isAppMobile || !canContinue || isSaving) {
      return;
    }
    void handleContinue();
  }, [canContinue, handleContinue, isAppMobile, isSaving]);

  const hitAreaEnabled = !collapseForKeyboard;
  const showDecorativeCaret = trimmed.length === 0;

  return (
    <QuizStepLayout
      testId="onboarding-name-quiz-step"
      className={isAppMobile ? "h-dvh max-h-dvh" : undefined}
      reserveFloatingCtaSpace={false}
      header={
        <QuizProgressHeader
          progressIndex={progressIndex}
          progressTotal={progressTotal}
          onBack={onBack}
        />
      }
    >
      <div
        ref={formRef}
        data-dismiss-kbd-scope
        data-testid="name-quiz-form"
        className="relative flex min-h-0 flex-1 flex-col md:flex-none"
        data-figma-node={trimmed ? "96121:8432" : "96121:8430"}
        data-figma-node-pc="96344:110242"
        data-keyboard-compact={collapseForKeyboard ? "true" : "false"}
        data-refocus-suppressed={refocusSuppressed ? "true" : "false"}
        data-device={device}
      >
        <div
          className={cn(
            "flex shrink-0 flex-col items-center gap-3 px-6 md:px-0",
            collapseForKeyboard ? "pt-2" : "pt-6",
          )}
          data-testid="name-quiz-prompt"
        >
          <EdHeroBubble
            layout="stacked"
            greeting={QUIZ_COMMON_COPY.edIntro}
            headline={I18n.t("What should I call you?")}
          />
        </div>

        {!collapseForKeyboard ? (
          <div
            aria-hidden
            className="min-h-0 flex-1 pointer-events-none"
            data-testid="name-quiz-input-spacer-top"
          />
        ) : null}

        <div
          className={cn(
            "relative shrink-0 max-md:px-0 md:flex-none md:px-0",
            hitAreaEnabled && NAME_QUIZ_INPUT_HIT_AREA_ENABLED_CLASS,
            collapseForKeyboard ? "mt-6" : undefined,
            NAME_QUIZ_PC_PROMPT_TO_INPUT_GAP_CLASS,
          )}
          data-testid="name-quiz-input-area"
          onMouseDown={handleHitAreaMouseDown}
          onClick={handleHitAreaClick}
        >
          <label className="sr-only" htmlFor="onboarding-name-input">
            {I18n.t("Your name")}
          </label>
          <div
            role="presentation"
            data-testid="name-quiz-input-hit-area"
            className={cn(
              "relative flex w-full items-center justify-center py-4",
              hitAreaEnabled && NAME_QUIZ_INPUT_HIT_AREA_ENABLED_CLASS,
              NAME_QUIZ_INPUT_HIT_AREA_CLASS,
            )}
            onMouseDown={handleHitAreaMouseDown}
            onClick={handleHitAreaClick}
          >
            <input
              ref={inputRef}
              id="onboarding-name-input"
              data-testid="onboarding-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder=""
              autoComplete="name"
              readOnly={isAppMobile && refocusSuppressed}
              tabIndex={isAppMobile && refocusSuppressed ? -1 : undefined}
              className={cn(
                "w-full border-none bg-transparent text-center",
                NAME_QUIZ_INPUT_HIT_AREA_CLASS,
                "headline_regular_m text-content_inverse_primary",
                "outline-none",
                showDecorativeCaret
                  ? "caret-transparent"
                  : "caret-content_aura_lv3_primary",
                isAppMobile && refocusSuppressed && "pointer-events-none",
              )}
            />
            {showDecorativeCaret ? (
              <span
                aria-hidden
                data-testid="name-quiz-caret"
                className={cn(
                  "pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-content_aura_lv3_primary",
                  NAME_QUIZ_DECORATIVE_CARET_CLASS,
                  styles.decorativeCaret,
                )}
              />
            ) : null}
          </div>
        </div>

        {!collapseForKeyboard ? (
          <div
            aria-hidden
            className="min-h-0 flex-1 pointer-events-none"
            data-testid="name-quiz-input-spacer-bottom"
          />
        ) : null}

        {/*
          App mobile: document-flow CTA (Welcome Name pattern).
          Keyboard compact: mt-auto docks Continue above keyboard (min pt-6 gap).
          PC: in-flow Continue with Figma 134px input→CTA gap.
        */}
        <div
          className={cn(
            "shrink-0",
            collapseForKeyboard && "mt-auto pt-6",
            ONBOARDING_FLOATING_CTA_INSET_NARROW_CLASS,
            "md:w-full md:px-0 md:pb-0",
            ONBOARDING_QUIZ_PC_INPUT_TO_CTA_GAP_CLASS,
            liftCtaForKeyboard
              ? "pb-keyboard transition-all duration-200"
              : ONBOARDING_FLOATING_CTA_BOTTOM_CLASS,
          )}
          data-testid="name-quiz-continue-area"
          onTouchStartCapture={handleContinueTouchStart}
          onTouchEndCapture={handleContinueTouchEnd}
        >
          <QuizContinueButton
            disabled={!canContinue}
            loading={isSaving}
            onClick={() => {
              if (isAppMobile) {
                return;
              }
              void handleContinue();
            }}
          />
        </div>
      </div>
    </QuizStepLayout>
  );
};
